import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export default function ChannelBackfillPage() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("Admin") || (user?.roles || []).includes("admin");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!isAdmin) return <div className="p-8 text-center"><p className="text-zinc-400">Admin access required.</p></div>;

  const runDiagnosis = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/channel-diagnosis");
      setReport(data);
      toast.success("Diagnosis complete");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const addChannel = async (userId, channelId, channelName, userName) => {
    if (!window.confirm(`Add "${channelName}" to ${userName}'s assigned channels?`)) return;
    try {
      await api.post("/admin/channel-backfill", { user_id: userId, channel_id: channelId });
      toast.success(`Added ${channelName} to ${userName}`);
      runDiagnosis();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="channel-backfill-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 mb-2">Channel Assignment Diagnosis</h1>
      <p className="text-sm text-zinc-500 mb-4">View each user's assigned channels and add any missing ones. Changes take effect immediately.</p>
      <Button onClick={runDiagnosis} disabled={loading} className="rounded-sm h-9 text-xs mb-6">{loading ? "Loading..." : "Run Diagnosis"}</Button>

      {report && report.users?.map(u => (
        <div key={u.email} className="border border-zinc-200 rounded-sm mb-4" data-testid={`user-${u.email}`}>
          <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200">
            <span className="font-semibold text-sm">{u.name}</span>
            <span className="text-xs text-zinc-500 ml-2">{u.email}</span>
            <span className="text-xs text-zinc-400 ml-2">({(u.roles || []).join(", ")})</span>
            <span className="text-xs text-zinc-500 ml-2">{u.assigned_count} channels assigned</span>
          </div>
          <div className="p-3">
            <p className="text-[10px] font-semibold uppercase text-zinc-400 mb-2">Currently Assigned:</p>
            <div className="flex flex-wrap gap-1 mb-3">
              {u.assigned_channels_detail?.map(ch => (
                <span key={ch._id} className="px-2 py-0.5 text-[10px] bg-green-100 text-green-700 rounded-sm">{ch.name}</span>
              ))}
              {(!u.assigned_channels_detail || u.assigned_channels_detail.length === 0) && <span className="text-[10px] text-zinc-400">No channels assigned</span>}
            </div>
            <p className="text-[10px] font-semibold uppercase text-zinc-400 mb-2">All Available Channels (click to add missing ones):</p>
            <div className="flex flex-wrap gap-1">
              {report.all_channels?.map(ch => {
                const isAssigned = u.assigned_channel_ids?.includes(ch._id);
                return (
                  <button key={ch._id} onClick={() => !isAssigned && addChannel(u._id, ch._id, ch.name, u.name)}
                    className={`px-2 py-0.5 text-[10px] rounded-sm border transition-colors ${isAssigned ? 'bg-green-50 border-green-200 text-green-600 cursor-default' : 'bg-white border-zinc-200 text-zinc-600 hover:bg-blue-50 hover:border-blue-300 cursor-pointer'}`}
                    disabled={isAssigned}>
                    {isAssigned ? "✓ " : "+ "}{ch.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
